import React, { useState, useRef, useEffect } from "react";
import {
  Table,
  DatePicker,
  Space,
  message,
  Input,
  Button,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { getAbsenKaryawan } from "../../api/transaksi/Absensi";

const LaporanAbsensiPage = () => {
  const [bulan, setBulan] = useState(dayjs());
  const [filteredData, setFilteredData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [dataAbsensi, setDataAbsensi] = useState([]);
  const searchInput = useRef(null);
  const tableRef = useRef();
  const [displayedRows, setDisplayedRows] = useState([]);

  let isAdmin = false;
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  if (
    sessiData &&
    Array.isArray(sessiData) &&
    sessiData[0]?.user?.[0]?.rolename
  ) {
    const adminRoles = JSON.parse(process.env.REACT_APP_ROLEADMIN || "[]");
    isAdmin = adminRoles.includes(sessiData[0].user[0].rolename);
  }

  useEffect(() => {
    fetchData();
  }, [bulan]);

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      const apiData = await getAbsenKaryawan(
        bulan.format("YYYY-MM"),
        isAdmin ? "" : sessiData[0].user[0].employeeId
      );
      if (apiData.length > 0) {
        const formattedData = apiData.map((item) => ({
          key: item.emp_id_karyawan,
          nama: item.emp_nama.trim(),
          bulan: item.abs_periode,
          total_hadir: Number(item.abs_total_hadir),
          total_ijin: Number(item.abs_total_ijin),
          total_sakit: Number(item.abs_total_sakit),
          total_cuti: Number(item.abs_total_cuti),
          total_alfa: Number(item.abs_total_alfa),
        }));

        setDataAbsensi(formattedData);
        setFilteredData(formattedData);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat mengambil data";
      message.error(errorMessage);
      setDataAbsensi([]);
      setFilteredData([]);
    } finally {
      setLoadingTable(false);
    }
  };

  const handleExportExcel = () => {
    const exportSource =
      displayedRows.length > 0 ? displayedRows : filteredData;
    if (exportSource.length === 0) {
      message.warning("Tidak ada data untuk diekspor.");
      return;
    }

    const exportData = exportSource.map(
      ({
        nama,
        total_hadir,
        total_cuti,
        total_sakit,
        total_ijin,
        total_alfa,
      }) => {
        const totalHariKerja =
          total_hadir + (total_cuti + total_sakit + total_ijin + total_alfa);
        return {
          "Nama Karyawan": nama,
          Hadir: total_hadir,
          Cuti: total_cuti,
          Sakit: total_sakit,
          Absen: total_ijin,
          Alfa: total_alfa,
          "Total Hari Kerja": totalHariKerja,
        };
      }
    );

    const finalExport = [...exportData];

    const worksheet = XLSX.utils.json_to_sheet(finalExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Absensi");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Laporan_Absensi_${bulan?.format("YYYY-MM") || "Semua"}.xlsx`);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Cari
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
  });

  const columns = [
    ...(isAdmin
      ? [
          {
            title: "Nama Karyawan",
            dataIndex: "nama",
            ...getColumnSearchProps("nama"),
          },
        ]
      : []),
    {
      title: "Hadir",
      dataIndex: "total_hadir",
      key: "total_hadir",
      sorter: (a, b) => a.total_hadir - b.total_hadir,
    },
    {
      title: "Cuti",
      dataIndex: "total_cuti",
      key: "total_cuti",
      sorter: (a, b) => a.total_cuti - b.total_cuti,
    },
    {
      title: "Sakit",
      dataIndex: "total_sakit",
      key: "total_sakit",
      sorter: (a, b) => a.total_sakit - b.total_sakit,
    },
    {
      title: "Ijin",
      dataIndex: "total_ijin",
      key: "total_ijin",
      sorter: (a, b) => a.total_ijin - b.total_ijin,
    },
    {
      title: "Alfa",
      dataIndex: "total_alfa",
      key: "total_alfa",
      sorter: (a, b) => a.total_alfa - b.total_alfa,
    },
    {
      title: "Total Hari Kerja",
      dataIndex: "totalHariKerja",
      key: "totalHariKerja",
      render: (_, record) => {
        const totalHari =
          record.total_hadir +
          (record.total_cuti +
            record.total_sakit +
            record.total_alfa +
            record.total_ijin);
        return totalHari;
      },
      sorter: (a, b) => a.totalHariKerja - b.totalHariKerja,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Divider>Laporan Absensi Karyawan</Divider>

      <Space style={{ marginBottom: 16 }}>
        <label>Bulan:</label>
        <DatePicker
          picker="month"
          value={bulan}
          onChange={(val) => setBulan(val)}
        />

        <Button
          icon={<FileExcelOutlined />}
          type="primary"
          onClick={handleExportExcel}
        >
          Export Excel
        </Button>
      </Space>

      <Table
        loading={loadingTable}
        ref={tableRef}
        dataSource={filteredData}
        columns={columns}
        rowKey="key"
        bordered
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
        onChange={(_, __, ___, extra) => {
          setDisplayedRows(extra.currentDataSource);
        }}
      />
    </div>
  );
};

export default LaporanAbsensiPage;
